import { ApolloClient, ApolloLink, InMemoryCache, Observable } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';
import { createClient as createSseClient } from 'graphql-sse';
import { PUBLIC_PATHNAMES } from '../lib/public-pathnames.js';
import { appDefaultOptions } from './app-default-options.js';
import { cacheTypePolicies } from './cache-type-policies.js';

const API_URL =
  (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? '/api';

const httpLink = new HttpLink({
  uri: API_URL,
  headers: { 'x-graphql-csrf': '1' },
});

const sseClient = createSseClient({ url: API_URL });

const sseLink = new ApolloLink((operation) => {
  return new Observable((sink) => {
    return sseClient.subscribe(
      { ...operation, query: print(operation.query) },
      {
        next: sink.next.bind(sink),
        error: sink.error.bind(sink),
        complete: sink.complete.bind(sink),
      },
    );
  });
});

const authErrorLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next: (response) => {
        const isUnauthenticated = response.errors?.some(
          (error) => error.extensions?.code === 'UNAUTHORIZED',
        );
        const isPublicPage = PUBLIC_PATHNAMES.has(window.location.pathname);
        if (isUnauthenticated && !isPublicPage) {
          window.location.replace('/login');
        } else {
          observer.next(response);
        }
      },
      error: observer.error.bind(observer),
      complete: observer.complete.bind(observer),
    });
    return () => sub.unsubscribe();
  });
});

const link = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  sseLink,
  httpLink,
);

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authErrorLink, link]),
  cache: new InMemoryCache({ typePolicies: cacheTypePolicies }),
  defaultOptions: appDefaultOptions,
});
